/**
 * 수수료 일괄 수정 엑셀 검증 — 레거시 `commissionBatchUpdateValidation.js` 포팅
 */
import type { CommissionBatchUpdateItem } from '@/entities/commission/api/commission.api';
import { parseCommissionRate } from '@/pages/settlement-fees/commissionBatchUtils';
import * as XLSX from 'xlsx';

export const COMMISSION_BATCH_UPDATE_REQUIRED_HEADERS = [
  '년월',
  '제품코드',
  '보험가',
  '제약사수수료율',
] as const;

const OPTIONAL_HEADERS = {
  note: '비고1(제약사 참고사항)',
  pharma: '제약사',
  drugName: '제품명',
  outOfStock: '품절여부',
  outOfStockFrom: '품절(From)',
  outOfStockTo: '품절(To)',
  incentive: '인센티브',
  incentiveNote: '인센티브메모',
} as const;

export type CommissionBatchValidationResult =
  | { valid: true; items: CommissionBatchUpdateItem[] }
  | {
      valid: false;
      reason: string;
      message: string;
      missingHeaders?: string[];
      rowErrors?: Array<{
        row: number;
        message?: string;
        missingValues?: string[];
      }>;
    };

const normalizeHeader = (value: unknown) => String(value ?? '').trim();
const normalizeYearMonth = (value: string) => value.replace(/\D/g, '');

const parsePrice = (value: string): number | null => {
  const parsed = Number(String(value).replace(/,/g, '').trim());
  return Number.isFinite(parsed) ? parsed : null;
};

const parseOutOfStock = (value: string) =>
  !value.trim() ? false : value.trim() === '1';

const parseApiDate = (value: string): string | undefined | null => {
  const text = value.trim();
  if (!text) return undefined;
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;
  if (/^\d{8}$/.test(text)) {
    return `${text.slice(0, 4)}-${text.slice(4, 6)}-${text.slice(6, 8)}`;
  }
  return null;
};

const isEmptyRow = (row: unknown[]) =>
  row.every((cell) => cell == null || String(cell).trim() === '');

export const formatCommissionBatchUpdateValidationMessage = (
  validation: CommissionBatchValidationResult,
): string => {
  if (validation.valid) return '';
  if (validation.reason === 'missing_headers' && validation.missingHeaders) {
    return `다음 필수 헤더가 없습니다: ${validation.missingHeaders.join(', ')}`;
  }
  if (validation.rowErrors?.length) {
    const preview = validation.rowErrors
      .slice(0, 5)
      .map((error) =>
        error.message
          ? `${error.row}행: ${error.message}`
          : `${error.row}행: 필수 값 누락 (${error.missingValues?.join(', ')})`,
      )
      .join('\n');
    const suffix =
      validation.rowErrors.length > 5
        ? `\n외 ${validation.rowErrors.length - 5}건`
        : '';
    return `${validation.message}\n${preview}${suffix}`;
  }
  return validation.message;
};

export const validateCommissionBatchUpdateFile = async (
  file: File,
): Promise<CommissionBatchValidationResult> => {
  const arrayBuffer = await file.arrayBuffer();
  const workbook = XLSX.read(arrayBuffer, { type: 'array', raw: false });
  const sheetName = workbook.SheetNames?.[0];
  if (!sheetName) {
    return {
      valid: false,
      reason: 'empty_file',
      message: '시트를 찾을 수 없습니다.',
    };
  }

  const rows = XLSX.utils.sheet_to_json<unknown[]>(workbook.Sheets[sheetName], {
    header: 1,
    defval: null,
  });

  if (!rows.length) {
    return {
      valid: false,
      reason: 'empty_file',
      message: '엑셀 파일에 데이터가 없습니다.',
    };
  }

  const headerRow = (rows[0] ?? []).map(normalizeHeader);
  const missingHeaders = COMMISSION_BATCH_UPDATE_REQUIRED_HEADERS.filter(
    (header) => !headerRow.includes(header),
  );
  if (missingHeaders.length > 0) {
    return {
      valid: false,
      reason: 'missing_headers',
      missingHeaders: [...missingHeaders],
      message: '필수 헤더가 없습니다.',
    };
  }

  const getCell = (row: unknown[], header: string) => {
    const index = headerRow.indexOf(header);
    if (index === -1) return '';
    const value = row[index];
    return value == null ? '' : String(value).trim();
  };

  const items: CommissionBatchUpdateItem[] = [];
  const rowErrors: NonNullable<
    Extract<CommissionBatchValidationResult, { valid: false }>['rowErrors']
  > = [];

  for (let rowIndex = 1; rowIndex < rows.length; rowIndex += 1) {
    const row = rows[rowIndex];
    if (!row || isEmptyRow(row)) continue;

    const excelRowNumber = rowIndex + 1;
    const missingValues = COMMISSION_BATCH_UPDATE_REQUIRED_HEADERS.filter(
      (header) => getCell(row, header) === '',
    );
    if (missingValues.length > 0) {
      rowErrors.push({ row: excelRowNumber, missingValues: [...missingValues] });
      continue;
    }

    const referenceYearMonth = normalizeYearMonth(getCell(row, '년월'));
    const drugCode = getCell(row, '제품코드');
    const price = parsePrice(getCell(row, '보험가'));
    const standardCommissionRate = parseCommissionRate(
      getCell(row, '제약사수수료율'),
    );

    if (referenceYearMonth.length !== 6) {
      rowErrors.push({
        row: excelRowNumber,
        message: '년월 형식이 올바르지 않습니다.',
      });
      continue;
    }
    if (price == null) {
      rowErrors.push({ row: excelRowNumber, message: '보험가가 숫자가 아닙니다.' });
      continue;
    }
    if (standardCommissionRate == null) {
      rowErrors.push({
        row: excelRowNumber,
        message: '제약사수수료율이 올바르지 않습니다.',
      });
      continue;
    }

    const outOfStockFrom = headerRow.includes(OPTIONAL_HEADERS.outOfStockFrom)
      ? parseApiDate(getCell(row, OPTIONAL_HEADERS.outOfStockFrom))
      : undefined;
    if (outOfStockFrom === null) {
      rowErrors.push({
        row: excelRowNumber,
        message: '품절(From) 날짜 형식이 올바르지 않습니다.',
      });
      continue;
    }

    const outOfStockTo = headerRow.includes(OPTIONAL_HEADERS.outOfStockTo)
      ? parseApiDate(getCell(row, OPTIONAL_HEADERS.outOfStockTo))
      : undefined;
    if (outOfStockTo === null) {
      rowErrors.push({
        row: excelRowNumber,
        message: '품절(To) 날짜 형식이 올바르지 않습니다.',
      });
      continue;
    }

    items.push({
      referenceYearMonth,
      pharmaCompanyName: headerRow.includes(OPTIONAL_HEADERS.pharma)
        ? getCell(row, OPTIONAL_HEADERS.pharma) || undefined
        : undefined,
      drugName: headerRow.includes(OPTIONAL_HEADERS.drugName)
        ? getCell(row, OPTIONAL_HEADERS.drugName) || undefined
        : undefined,
      drugCode,
      price,
      standardCommissionRate,
      standardCommissionNote: headerRow.includes(OPTIONAL_HEADERS.note)
        ? getCell(row, OPTIONAL_HEADERS.note) || undefined
        : undefined,
      incentive: headerRow.includes(OPTIONAL_HEADERS.incentive)
        ? getCell(row, OPTIONAL_HEADERS.incentive) || undefined
        : undefined,
      incentiveNote: headerRow.includes(OPTIONAL_HEADERS.incentiveNote)
        ? getCell(row, OPTIONAL_HEADERS.incentiveNote) || undefined
        : undefined,
      outOfStock: headerRow.includes(OPTIONAL_HEADERS.outOfStock)
        ? parseOutOfStock(getCell(row, OPTIONAL_HEADERS.outOfStock))
        : false,
      outOfStockFrom,
      outOfStockTo,
    });
  }

  if (rowErrors.length > 0) {
    return {
      valid: false,
      reason: 'row_validation',
      rowErrors,
      message: '데이터 유효성 검사에 실패했습니다.',
    };
  }

  if (items.length === 0) {
    return {
      valid: false,
      reason: 'no_data',
      message: '업로드할 데이터 행이 없습니다.',
    };
  }

  return { valid: true, items };
};
